{
  "flow": {
    "ariaLabel": "KYB steps",
    "steps": {
      "registration": {
        "label": "Registration declared",
        "hint": "The company lists its legal registration (country and province / free zone)."
      },
      "check": {
        "label": "Provider check run",
        "hint": "A moderator chooses a provider for the jurisdiction and runs it."
      },
      "decision": {
        "label": "Moderator decision",
        "hint": "Hits reviewed one by one, then the check is confirmed or dismissed."
      },
      "badge": {
        "label": "Badge awarded",
        "hint": "A moderator awards a badge — only one the evidence supports."
      }
    }
  },
  "howItWorks": {
    "title": "How KYB works",
    "rules": {
      "moderatorChooses": "Providers are tools for moderators. The list recommends providers for the company's jurisdiction, but you choose which one to run. Nothing paid runs automatically.",
      "evidenceOnly": "Provider results are evidence only. Each check records exactly what it covered (registry, officers, owners, sanctions screening…), and only those facts count.",
      "badgeLimited": "You award the badge, but only a badge whose required evidence holds. A badge claims only what was checked.",
      "override": "A super moderator may award without the evidence, with a recorded reason.",
      "pause": "A badge pauses by itself when its evidence lapses (e.g. a registration is edited and goes back to review), and expires after its validity period.",
      "gate": "The verdict (verified, pending, rejected, expired, none) feeds the contract gate and the compliance criterion in matching. Matching and messaging are never blocked."
    }
  },
  "verdict": {
    "prefix": "Verdict: {{verdict}}",
    "verified": "Verified",
    "pending": "Pending",
    "rejected": "Rejected",
    "expired": "Expired",
    "none": "Not started",
    "help": {
      "verified": "The public badge requires a registry match and a clear screening. Contracts pass the KYB gate.",
      "pending": "Some check or award exists, but no badge proves registry and screening yet (or the badge is paused).",
      "rejected": "The latest decided check was rejected (registry mismatch or a confirmed hit).",
      "expired": "The badge that proved registry and screening has expired. Run a fresh check and award again.",
      "none": "Nothing has been checked yet."
    }
  },
  "useCase": {
    "registry_lookup": "Registry",
    "officers": "Officers",
    "ubo": "Beneficial owners",
    "aml_company": "AML (company)",
    "aml_people": "AML (people)",
    "documents": "Documents",
    "monitoring": "Monitoring"
  },
  "queue": {
    "title": "KYB",
    "subtitle": "Companies by verification state. Open one to choose a provider, review results and award a badge.",
    "badgeTypes": "Badge types",
    "searchPlaceholder": "Search by name or registration number",
    "loadError": "Could not load the KYB queue.",
    "empty": "No companies in this state.",
    "notApproved": "not approved",
    "noRegistration": "No registration",
    "noEvidence": "No evidence yet",
    "noBadge": "No badge",
    "suggested": "Evidence supports: {{badge}}",
    "tabs": {
      "all": "All",
      "needs_check": "Needs a check",
      "in_progress": "In progress",
      "awaiting_decision": "Awaiting decision",
      "awaiting_badge": "Awaiting badge",
      "verified": "Verified",
      "checks": "Check log"
    },
    "stateHelp": {
      "needs_check": "Never checked, or the last check was rejected, dismissed or failed, or only part of the evidence holds.",
      "in_progress": "A provider is still working, or a moderator must pick the right registry company.",
      "awaiting_decision": "Results are in. A super moderator reviews the hits and confirms or dismisses the check.",
      "awaiting_badge": "The evidence supports a stronger badge than the one the company shows.",
      "verified": "The public badge proves a registry match and a clear screening."
    },
    "state": {
      "needs_check": "Needs a check",
      "in_progress": "In progress",
      "awaiting_decision": "Awaiting decision",
      "awaiting_badge": "Awaiting badge",
      "verified": "Verified"
    },
    "columns": {
      "company": "Company",
      "registrations": "Registrations",
      "state": "State",
      "evidence": "Evidence",
      "badge": "Badge",
      "next": "Next step"
    },
    "nextAction": {
      "askRegistration": "Ask the company to add its registration",
      "award": "Award \"{{badge}}\"",
      "run": "Run {{provider}} (recommended)",
      "manual": "Record registry evidence by hand",
      "none": "Nothing to do"
    },
    "log": {
      "decisionLabel": "Decision",
      "statusLabel": "Status",
      "allDecisions": "All decisions",
      "allStatuses": "All statuses",
      "empty": "No checks yet.",
      "columns": {
        "check": "Check",
        "provider": "Provider",
        "covered": "Covered",
        "created": "Created"
      }
    }
  },
  "company": {
    "back": "← KYB queue",
    "profile": "Company profile",
    "invalidId": "Invalid company id.",
    "loadError": "Could not load this company's KYB.",
    "spend": "Provider spend: {{spent}} of {{budget}} budget",
    "registrationsTitle": "Registrations",
    "registrationsHelp": "Routing uses the registration's country and province or free zone. Edits by the company send it back to review.",
    "subdivisionHint": "A registration without a province or free zone routes on the country alone, so recommendations are less precise.",
    "checksTitle": "Checks ({{count}})",
    "checksHelp": "Newest first. Each check shows what it covered, what the registry returned and how it compares to what the company declared.",
    "noChecks": "No checks yet. Choose a provider above."
  },
  "providers": {
    "title": "Choose a provider",
    "help": "Recommended for this jurisdiction first. These are recommendations only: you may run any configured provider, as often as you need.",
    "registration": "Registration to check",
    "noRegistration": "This company has no entity registration yet, so no provider can run. Ask the company to add one under Trust & Compliance → Add Entity.",
    "loadError": "Could not load providers.",
    "startError": "Could not start the check.",
    "starting": "Starting…",
    "run": "Run check",
    "enterEvidence": "Enter evidence",
    "openCheck": "Open check #{{id}}",
    "recommended": "Recommended #{{rank}}",
    "notIntegrated": "Benchmarked, not integrated",
    "notConfigured": "No credentials",
    "badgeEligible": "Counts toward badges",
    "notBadgeEligible": "Doesn't count toward badges",
    "bestFor": "Best for: {{text}}",
    "cost": "Cost: {{text}}",
    "candidateSelection": "May ask you to pick the company",
    "webhook": "Callbacks: {{kind}}",
    "benchmarkHelp": "Benchmark score by region (0–5) from the provider benchmark.",
    "showBenchmarked_one": "Show {{count}} benchmarked provider that isn't integrated",
    "showBenchmarked_other": "Show {{count}} benchmarked providers that aren't integrated",
    "hideBenchmarked": "Hide providers that aren't integrated",
    "role": {
      "primary": "Primary for this jurisdiction",
      "backup": "Backup",
      "free": "Free source",
      "manual": "Manual fallback"
    },
    "route": {
      "title": "Route: {{name}} ({{jurisdiction}})",
      "primary": "Primary: {{list}}",
      "backup": "Backup: {{list}}",
      "free": "Free sources: {{list}}",
      "official": "Official registry: {{source}}",
      "owners": "Beneficial owners: {{owners}}"
    },
    "manual": {
      "help": "Record what you found in the official registry yourself. It counts as a registry match only when the number matches and the entity is active.",
      "missing": "Registry name, link, matched name and number are required.",
      "submit": "Record evidence",
      "fields": {
        "registryName": "Registry name",
        "registryUrl": "Link to the registry entry",
        "matchedName": "Name in the registry",
        "registrationNumber": "Registration number",
        "entityStatus": "Entity status",
        "note": "Note"
      }
    },
    "blocked": {
      "noRegistration": "Can't run yet: the company has no entity registration. A check verifies a registration, so the company adds one under Trust & Compliance → Add Entity (country, province or free zone, number).",
      "noCredentials": "Can't run on this server: its credentials aren't set (e.g. THEKYB_API_TOKEN)."
    }
  },
  "check": {
    "titleKyb": "Check #{{id}} · {{provider}}",
    "titleMonitoring": "Monitoring alert #{{id}} · {{provider}}",
    "created": "Created {{date}}",
    "completed": "completed {{date}}",
    "requestedBy": "by {{name}}",
    "cost": "cost {{cost}}",
    "vendorRef": "ref {{ref}}",
    "decidedBy": "Decided by {{name}} on {{date}}",
    "covered": "Covered:",
    "notBadgeEligible": "Doesn't count toward badges",
    "inFlight": "The provider is still working. This page checks again every 10 seconds.",
    "refreshError": "Could not refresh.",
    "appliedFields_one": "{{count}} field applied",
    "appliedFields_other": "{{count}} fields applied",
    "hitsTitle": "Screening hits ({{count}})",
    "noHits": "No hits.",
    "actions": {
      "refresh": "Ask the provider now",
      "refreshing": "Asking…"
    },
    "status": {
      "pending": "Queued",
      "running": "Running",
      "needs_selection": "Pick the company",
      "clear": "Clear",
      "review": "Needs review",
      "failed": "Failed"
    },
    "decision": {
      "pending": "Undecided",
      "approved": "Approved",
      "rejected": "Rejected",
      "dismissed": "Dismissed"
    },
    "hitDecision": {
      "pending": "Undecided",
      "confirmed": "True hit",
      "dismissed": "False positive"
    },
    "hitCategory": {
      "sanctions": "Sanctions",
      "pep": "PEP",
      "adverse_media": "Adverse media"
    },
    "hit": {
      "score": "Score {{score}}",
      "subject": "Subject: {{name}} ({{type}})",
      "list": "List: {{name}}",
      "confirm": "Confirm true hit",
      "dismiss": "False positive",
      "reasonPlaceholder": "Reason (required)",
      "reasonRequired": "Please add a short reason.",
      "decideError": "Could not save the decision."
    },
    "candidates": {
      "title": "Which company is it? ({{count}} candidates)",
      "help": "The registry returned several companies and none matched the registration number exactly.",
      "name": "Name",
      "number": "Number",
      "status": "Status",
      "registered": "Registered",
      "choose": "This one",
      "noneOfThese": "None of these? Dismiss the check and try another provider or the manual one.",
      "error": "Could not select the candidate."
    },
    "registry": {
      "title": "Registry result",
      "registeredName": "Registered name",
      "number": "Number",
      "registry": "Registry",
      "entityStatus": "Entity status",
      "selected": "Chosen candidate",
      "person": "Person",
      "role": "Role",
      "ownership": "Ownership",
      "active": "Active",
      "source": {
        "officer": "Officer",
        "ubo": "Owner"
      }
    },
    "comparison": {
      "title": "Declared vs registry",
      "help": "Informational: what the company told us next to what the registry says.",
      "declared": "Declared",
      "registry": "Registry",
      "match": "Match",
      "notDeclared": "Not declared",
      "noRegistryPeople": "The registry returned no officers or owners to compare with.",
      "fields": {
        "legalName": "Legal name",
        "registrationNumber": "Registration number",
        "shareholder": "Shareholder",
        "undeclared": "Registry person"
      },
      "matchValue": {
        "same": "Same",
        "similar": "Similar",
        "different": "Different",
        "unknown": "Unknown",
        "not_in_registry": "Not in registry"
      }
    },
    "proposal": {
      "title": "Proposed compliance values",
      "help": "Only fields this check covered. Confirming applies them to the company record.",
      "applied": "(applied)"
    },
    "decide": {
      "title": "Decision",
      "superOnly": "A super moderator reviews the hits and confirms or dismisses this check.",
      "pendingHits_one": "Decide the {{count}} open hit before confirming.",
      "pendingHits_other": "Decide the {{count}} open hits before confirming.",
      "preview": {
        "approved": "Confirming will record this check as approved: the registry matched and no hit was confirmed. The evidence it covered then counts toward badges.",
        "rejected": "Confirming will record this check as rejected (the registry didn't match, or a hit was confirmed). The company's verdict becomes rejected."
      },
      "dismissHelp": "Dismissing closes the check without a verdict, so another check with this provider can run.",
      "reasonPlaceholder": "Reason (optional)",
      "confirm": "Confirm",
      "confirming": "Confirming…",
      "dismiss": "Dismiss",
      "dismissing": "Dismissing…",
      "confirmError": "Could not confirm.",
      "dismissError": "Could not dismiss."
    }
  },
  "badges": {
    "evidenceTitle": "Evidence",
    "evidenceHelp": "Facts that hold now. Each provider fact comes from the latest decided check that covered it.",
    "holds": "holds",
    "missing": "missing",
    "title": "Award a badge",
    "help": "Badges claim only what was checked. The suggested badge is the strongest one the evidence supports.",
    "noPublicBadge": "No public badge",
    "validUntil": "Valid until {{date}}",
    "awardedBy": "awarded by {{name}}",
    "suggested": "Suggested",
    "current": "Current",
    "award": "Award",
    "overrideAward": "Award with override",
    "blocked": "Missing evidence: {{facts}}",
    "overrideWarning": "The required evidence doesn't hold. You are awarding with a super-moderator override; the reason is recorded.",
    "note": "Note (optional)",
    "validUntilLabel": "Valid until (optional)",
    "validUntilHelp": "Default: one year from today.",
    "overrideReason": "Override reason (required)",
    "awarding": "Awarding…",
    "confirmAward": "Award \"{{label}}\"",
    "awardError": "Could not award the badge.",
    "noneSeeded": "No badge types exist yet. Run manage.py seed_verification_badges on the backend.",
    "loadError": "Could not load the badge panel.",
    "historyTitle": "Award history ({{count}})",
    "noHistory": "No awards yet.",
    "state": {
      "active": "Active",
      "paused": "Paused",
      "expired": "Expired",
      "revoked": "Revoked"
    },
    "overridden": "Override",
    "awardedOn": "Awarded {{date}} by {{name}}, valid until {{until}}",
    "pausedHelp": "Paused: the evidence it requires no longer holds. It shows again once the evidence is back.",
    "noteLine": "Note: {{note}}",
    "overrideLine": "Override reason: {{reason}}",
    "revokedLine": "Revoked {{date}} by {{name}}: {{reason}}",
    "revoke": "Revoke",
    "revokeReason": "Reason (required)",
    "confirmRevoke": "Revoke award",
    "revokeError": "Could not revoke."
  },
  "monitoring": {
    "title": "Ongoing monitoring",
    "on": "On",
    "off": "Off",
    "help": "Uses the default provider ({{provider}}). Alerts arrive as monitoring checks to decide like any other.",
    "since": "Since {{date}}.",
    "turnOn": "Turn on",
    "turnOff": "Turn off",
    "superOnly": "Super moderators turn monitoring on or off."
  },
  "fields": {
    "title": "Compliance values on the company",
    "help": "Set by confirmed checks (provider) or typed by a moderator.",
    "source": {
      "provider": "Provider (confirmed)",
      "manual": "Moderator",
      "unset": "Not set"
    }
  },
  "field": {
    "tier1BasicVerification": "Tier 1 — Basic verification",
    "tier2StandardCompliance": "Tier 2 — Standard compliance",
    "tier3AdvancedDueDiligence": "Tier 3 — Advanced due diligence",
    "pepMatches": "PEP matches",
    "amlAlerts": "AML alerts",
    "lastScreening": "Last screening",
    "overallComplianceStatus": "Overall compliance status",
    "overallComplianceScore": "Overall compliance score"
  },
  "badgeTypes": {
    "title": "Badge types",
    "subtitle": "Badges are data: a public label, a rank and the evidence facts they require. Moderators award them per company.",
    "add": "New badge",
    "factsTitle": "Evidence facts",
    "factsHelp": "What a badge can require. Facts are computed by the backend; adding one is a code change.",
    "loadError": "Could not load badge types.",
    "rank": "Rank {{rank}}",
    "inactive": "Inactive",
    "requires": "Requires:",
    "awardCount_one": "Awarded {{count}} time",
    "awardCount_other": "Awarded {{count}} times",
    "newTitle": "New badge",
    "editTitle": "Edit badge",
    "keyLocked": "The key is fixed once a badge has been awarded.",
    "saveError": "Could not save the badge.",
    "deleteError": "Could not delete the badge.",
    "fields": {
      "key": "Key",
      "label": "Public label",
      "rank": "Rank (higher = stronger)",
      "active": "Active",
      "description": "Description",
      "requiredEvidence": "Required evidence"
    }
  },
  "companyView": {
    "title": "Business verification (KYB)",
    "noBadge": "No badge yet",
    "text": {
      "noRegistration": "Add your company's official registration below. MatchPoint checks it against the official registry before showing a verification badge to partners.",
      "not_started": "Your registration is on file. A MatchPoint moderator will check it against the official registry and sanctions lists.",
      "in_progress": "MatchPoint is checking your company. Nothing is needed from you unless we get in touch.",
      "verified": "Your company is verified. Partners see the badge on your profile and match cards.",
      "action_needed": "We couldn't confirm your company from the official sources. Please check your registration details, or contact support."
    },
    "steps": {
      "registration": {
        "label": "Add your registration",
        "hint": "Country, province or free zone, and number."
      },
      "check": {
        "label": "Registry & screening check",
        "hint": "MatchPoint checks the official registry and sanctions lists."
      },
      "review": {
        "label": "Moderator review",
        "hint": "A person reviews every result. Nothing is automatic."
      },
      "badge": {
        "label": "Badge shown to partners",
        "hint": "It says exactly what was checked — nothing more."
      }
    },
    "badgeMeans": "What your badge says: {{text}}",
    "privacy": "Partners never see check details, scores or screening results — only the badge.",
    "updated": "Last update {{date}}."
  },
  "gate": {
    "warningTitle": "KYB check before this contract is binding",
    "blockedTitle": "This contract can't be accepted yet",
    "warningHelp": "Both companies should be verified by MatchPoint before the contract becomes binding. For now the contract can still be accepted.",
    "blockedHelp": "Both companies must be verified by MatchPoint before the contract can be accepted. Your acceptance was not recorded.",
    "verdict": "verdict: {{verdict}}"
  },
  "dealGate": {
    "title": "KYB contract gate",
    "help": "The contract becomes binding when both owners accept it. Both companies should be verified first: in warn mode (the default) it goes through with warnings, in enforce mode it is refused until they are, or a super moderator records an override. Marking it completed is gated the same way.",
    "side": {
      "buyer": "Buyer",
      "supplier": "Supplier"
    },
    "lastSaveClear": "Last contract save: both companies verified, no KYB warnings.",
    "noContract": "No contract on this deal yet.",
    "override": {
      "title": "Override (super moderator)",
      "help": "Lets this contract become binding although a company isn't verified. The reason is stored on the contract and never shown to the companies.",
      "active": "Override by {{name}} on {{date}}: {{reason}}",
      "reason": "Reason (required)",
      "record": "Record override",
      "clear": "Clear override",
      "error": "Could not save the override."
    }
  }
}
